import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScrollMode, SpecialZoomLevel, Viewer } from "@react-pdf-viewer/core";
import { type HighlightArea, highlightPlugin, type RenderHighlightsProps, Trigger } from "@react-pdf-viewer/highlight";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import * as Sentry from "@sentry/react";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { ViewerWrapper } from "@/components/feature/pdf/viewerWrapper";
import { getPdfContent } from "@/components/feature/pdf/helpers";
import { CitationInfo } from "@/views/chat/types";

interface PDFViewerProps {
  citationHighlight: CitationInfo | null;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ citationHighlight }) => {
  const { currentPdfId } = useCurrentPdf();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const [areas, setAreas] = useState<HighlightArea[]>([]);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const lastAppliedCitationRef = useRef<CitationInfo | null>(null);

  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;

  const renderHighlights = useCallback(
    (props: RenderHighlightsProps) => (
      <div>
        {areas
          .filter(area => area.pageIndex === props.pageIndex)
          .map((area, idx) => (
            <div
              key={idx}
              className="highlight-area"
              style={{
                background: "yellow",
                opacity: 0.4,
                mixBlendMode: "multiply",
                ...props.getCssProperties(area, props.rotation),
              }}
            />
          ))}
      </div>
    ),
    [areas],
  );

  const highlightPluginInstance = highlightPlugin({
    renderHighlights,
    trigger: Trigger.None,
  });

  const jumpToHighlightArea = useCallback(
    (area: HighlightArea) => {
      highlightPluginInstance.jumpToHighlightArea(area);
    },
    [highlightPluginInstance],
  );

  const handleHighlight = useCallback(
    (highlightAreas: HighlightArea[], page: number) => {
      setAreas(highlightAreas);
      if (highlightAreas.length > 0) {
        jumpToHighlightArea(highlightAreas[0]);
        setTimeout(() => {
          document
            .querySelector(`[data-page-number="${page}"]`)
            ?.scrollIntoView({ behavior: "instant", block: "center", inline: "center" });
        }, 200);
      }
    },
    [jumpToHighlightArea],
  );

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setError(null);
        if (!currentPdfId) {
          return;
        }

        const response = await getPdfContent(currentPdfId);

        const objectUrl = URL.createObjectURL(response.data);
        setPdfUrl(objectUrl);
        setIsDocumentLoaded(false);
        lastAppliedCitationRef.current = null;
      } catch (error) {
        Sentry.captureException(error);
        setError(error instanceof Error ? error.message : "An unknown error occurred");
        setPdfUrl(null);
      }
    };

    if (currentPdfId) {
      void fetchPdf();
    }
  }, [currentPdfId]);

  useEffect(() => {
    if (
      isDocumentLoaded &&
      citationHighlight &&
      citationHighlight.fseId === currentPdfId &&
      (!lastAppliedCitationRef.current ||
        JSON.stringify(lastAppliedCitationRef.current) !== JSON.stringify(citationHighlight))
    ) {
      const highlight: HighlightArea = {
        pageIndex: citationHighlight.page,
        left: citationHighlight.boundingRect.left,
        top: citationHighlight.boundingRect.top,
        width: citationHighlight.boundingRect.width,
        height: citationHighlight.boundingRect.height,
      };
      handleHighlight([highlight], citationHighlight.page - 1);
      lastAppliedCitationRef.current = citationHighlight;
    }
  }, [citationHighlight, currentPdfId, isDocumentLoaded, handleHighlight]);

  if (error) {
    return <div className="flex h-full items-center justify-center text-red-500">{error}</div>;
  }

  if (!pdfUrl) {
    return <div className="flex h-full items-center justify-center">Loading PDF...</div>;
  }

  return (
    <ViewerWrapper>
      <div
        style={{
          border: "1px solid rgba(0, 0, 0, 0.3)",
          height: "92vh",
        }}
      >
        <Toolbar />
        <Viewer
          fileUrl={pdfUrl}
          plugins={[highlightPluginInstance, toolbarPluginInstance]}
          defaultScale={SpecialZoomLevel.PageWidth}
          onDocumentLoad={() => {
            console.log("PDF document loaded");
            setIsDocumentLoaded(true);
          }}
          ref={ref => {
            viewerRef.current = ref;
            console.log("Viewer reference set");
          }}
          scrollMode={ScrollMode.Vertical}
          enableSmoothScroll={true}
        />
      </div>
    </ViewerWrapper>
  );
};
